import type {
  IHelpdeskCreateTicketRequest,
  IHelpdeskLimits,
  IHelpdeskTicketContext,
  IHelpdeskTicketDetail
} from "@kuika/kuika-cl-model";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { HelpdeskService } from "../../../services/helpdesk-service";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaCollapsePanel from "../../ui-kit/kuika-collapse-panel/kuika-collapse-panel";
import KuikaField from "../../ui-kit/kuika-field/kuika-field";
import KuikaInfoBox from "../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaSmallText from "../../ui-kit/typography/SmallText/SmallText";
import KuikaTextMedium from "../../ui-kit/typography/TextMedium/TextMedium";
import {
  clearHelpdeskDraft,
  helpdeskDraftKey,
  IHelpdeskDraft,
  readHelpdeskDraft,
  writeHelpdeskDraft
} from "./helpdesk-draft";
import HelpdeskDropzone from "./helpdesk-dropzone";
import { isAccountRestricted, readHelpdeskError } from "./helpdesk-errors";
import {
  describeRejectedFile,
  formatFileSize,
  IHelpdeskFileSelection,
  IHelpdeskRejectedFile,
  selectFiles,
  totalsOf
} from "./helpdesk-files";
import {
  hasErrors,
  IHelpdeskNewTicketErrors,
  IHelpdeskNewTicketFields,
  validateNewTicket
} from "./helpdesk-new-ticket-validation";
import HelpdeskPendingFiles from "./helpdesk-pending-files";
import HelpdeskRestrictedNotice from "./helpdesk-restricted-notice";
import {
  describeFailedUploads,
  IHelpdeskFailedUpload,
  IHelpdeskUploadProgress,
  newHelpdeskKey,
  uploadHelpdeskFiles
} from "./helpdesk-upload";
import { HELPDESK_UPLOAD_DEPS } from "./helpdesk-upload-deps";

export interface HelpdeskNewTicketFormProps {
  userId: string | undefined;
  /** The workspace the user is in; shown in the context block (the server records the verified one). */
  workspaceName: string | undefined;
  limits: IHelpdeskLimits;
  /** The app and screen the user is on, read when the form opens. */
  getTicketContext: () => IHelpdeskTicketContext;
  onCreated: (ticket: IHelpdeskTicketDetail) => void;
  onCancel: () => void;
}

interface IContextRow {
  label: string;
  value: string;
}

const EMPTY_FIELDS: IHelpdeskNewTicketFields = { subject: "", description: "", appMail: "", appPassword: "" };
const DRAFT_SAVE_DELAY_MS: number = 500;

/**
 * The new-ticket form of the Kuika ITSM, rebuilt on the designer ui-kit (ITSM
 * `frontend/src/components/NewTicketForm.tsx`). The customer only describes the request; type,
 * priority and reason are set by the support agent who picks it up.
 */
const HelpdeskNewTicketForm = ({
  userId,
  workspaceName,
  limits,
  getTicketContext,
  onCreated,
  onCancel
}: HelpdeskNewTicketFormProps): React.ReactElement => {
  const draftKey: string = helpdeskDraftKey(userId);
  const [fields, setFields] = useState<IHelpdeskNewTicketFields>(EMPTY_FIELDS);
  const [errors, setErrors] = useState<IHelpdeskNewTicketErrors>({});
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [progress, setProgress] = useState<IHelpdeskUploadProgress | null>(null);
  const [draftRestored, setDraftRestored] = useState<boolean>(false);
  const [restricted, setRestricted] = useState<boolean>(false);
  // One key per form: re-sending after a lost response replays the ticket instead of opening a second one.
  const submitKey: React.MutableRefObject<string> = useRef<string>(newHelpdeskKey());
  const draftLoaded: React.MutableRefObject<boolean> = useRef<boolean>(false);

  const ticketContext: IHelpdeskTicketContext = useMemo(
    (): IHelpdeskTicketContext => getTicketContext(),
    // Read once when the form opens: the ticket describes where the user was when they started it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const contextRows: IContextRow[] = [
    { label: "Workspace", value: workspaceName ?? "" },
    { label: "App", value: ticketContext.appName ?? "" },
    { label: "Screen", value: ticketContext.screenName ?? "" }
  ].filter((row: IContextRow): boolean => !!row.value);

  useEffect((): void => {
    const draft: IHelpdeskDraft | null = readHelpdeskDraft(draftKey);
    if (draft) {
      setFields((current: IHelpdeskNewTicketFields): IHelpdeskNewTicketFields => ({ ...current, ...draft }));
      setDraftRestored(true);
    }
    draftLoaded.current = true;
  }, [draftKey]);

  useEffect((): (() => void) => {
    const timer: ReturnType<typeof setTimeout> = setTimeout((): void => {
      if (draftLoaded.current) {
        writeHelpdeskDraft(draftKey, { subject: fields.subject, description: fields.description });
      }
    }, DRAFT_SAVE_DELAY_MS);
    return (): void => clearTimeout(timer);
  }, [draftKey, fields.subject, fields.description]);

  const setField = (name: keyof IHelpdeskNewTicketFields, value: string): void => {
    setFields((current: IHelpdeskNewTicketFields): IHelpdeskNewTicketFields => ({ ...current, [name]: value }));
    if (errors[name]) {
      setErrors((current: IHelpdeskNewTicketErrors): IHelpdeskNewTicketErrors => ({ ...current, [name]: undefined }));
    }
  };

  const addFiles = (picked: File[]): void => {
    // A new ticket holds nothing yet but the files already picked.
    const selection: IHelpdeskFileSelection = selectFiles(picked, limits, totalsOf(files));
    selection.rejected.forEach((rejected: IHelpdeskRejectedFile): void => {
      KMainFunctions.displayWarningNotification(describeRejectedFile(rejected, limits));
    });
    if (selection.accepted.length > 0) {
      setFiles((current: File[]): File[] => [...current, ...selection.accepted]);
    }
  };

  const discardDraft = (): void => {
    setFields(EMPTY_FIELDS);
    setErrors({});
    clearHelpdeskDraft(draftKey);
    setDraftRestored(false);
  };

  const submit = async (): Promise<void> => {
    const validation: IHelpdeskNewTicketErrors = validateNewTicket(fields, limits);
    setErrors(validation);
    if (hasErrors(validation) || submitting) return;

    setSubmitting(true);
    try {
      const request: IHelpdeskCreateTicketRequest = {
        subject: fields.subject.trim(),
        description: fields.description,
        appMail: fields.appMail.trim() || undefined,
        appPassword: fields.appPassword || undefined,
        context: {
          appName: ticketContext.appName,
          appId: ticketContext.appId,
          screenName: ticketContext.screenName,
          screenId: ticketContext.screenId
        }
      };
      const ticket: IHelpdeskTicketDetail = await HelpdeskService.createTicket(request, submitKey.current);

      let failed: IHelpdeskFailedUpload[] = [];
      if (files.length > 0) {
        failed = ticket.initialMessageId
          ? await uploadHelpdeskFiles(
              ticket.ticketNumber,
              ticket.initialMessageId,
              files,
              HELPDESK_UPLOAD_DEPS,
              setProgress
            )
          : files.map((file: File): IHelpdeskFailedUpload => ({ fileName: file.name, reason: "other" }));
      }

      clearHelpdeskDraft(draftKey);
      if (failed.length > 0) {
        KMainFunctions.displayWarningNotification(
          `The ticket was created, but these files could not be uploaded: ${describeFailedUploads(failed)}`
        );
      } else {
        KMainFunctions.displaySuccessNotification(`Your ticket ${ticket.referenceCode} has been created.`);
      }
      onCreated(ticket);
    } catch (error) {
      if (isAccountRestricted(error)) {
        setRestricted(true);
      } else {
        KMainFunctions.displayErrorNotification(readHelpdeskError(error).message);
      }
    } finally {
      setSubmitting(false);
      setProgress(null);
    }
  };

  if (restricted) {
    return (
      <div className="kuika-helpdesk-new-ticket">
        <HelpdeskRestrictedNotice />
      </div>
    );
  }

  return (
    <div className="kuika-helpdesk-new-ticket" data-testid="helpdeskNewTicketForm">
      <div className="kuika-helpdesk-new-ticket__intro">
        <KuikaTextMedium>New Ticket</KuikaTextMedium>
        <KuikaSmallText className="kuika-helpdesk-new-ticket__subtitle">
          Describe your request in detail and attach videos, images or documents if needed. Our support team will set
          the type and priority.
        </KuikaSmallText>
      </div>

      {contextRows.length > 0 && (
        <KuikaInfoBox
          color="blue"
          leftUnit={
            <div className="kuika-helpdesk-new-ticket__context" data-testid="helpdeskTicketContext">
              <div className="kuika-flex--gap-xs kuika-align-center">
                <KuikaSvg name="info-circle" width={14} height={14} color="var(--blue-5)" />
                <KuikaSmallText>Kuika app details will be attached automatically</KuikaSmallText>
              </div>
              <dl className="kuika-helpdesk-new-ticket__context-grid">
                {contextRows.map((row: IContextRow): React.ReactElement => (
                  <React.Fragment key={row.label}>
                    <dt>{row.label}</dt>
                    <dd>{row.value}</dd>
                  </React.Fragment>
                ))}
              </dl>
            </div>
          }
        />
      )}

      {draftRestored && (
        <KuikaInfoBox
          color="blue"
          leftUnit={<KuikaSmallText>Draft restored</KuikaSmallText>}
          rightUnit={
            <KuikaButton type="text" size="sm" onClick={discardDraft} disabled={submitting}>
              Clear
            </KuikaButton>
          }
        />
      )}

      <KuikaField
        label="Subject"
        htmlFor="helpdesk-new-ticket-subject"
        error={errors.subject}
        counter={{ current: fields.subject.trim().length, max: limits.subjectMaxLength }}
      >
        <KuikaInput
          id="helpdesk-new-ticket-subject"
          data-testid="ticketTitleInput"
          placeholder="Summarize your request briefly"
          value={fields.subject}
          disabled={submitting}
          error={!!errors.subject}
          aria-invalid={!!errors.subject}
          onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number): void =>
            setField("subject", String(value))
          }
        />
      </KuikaField>

      <KuikaField
        label="Description"
        htmlFor="helpdesk-new-ticket-description"
        error={errors.description}
        counter={{ current: fields.description.length, max: limits.descriptionMaxLength }}
      >
        <KuikaInput
          id="helpdesk-new-ticket-description"
          data-testid="ticketMessageInput"
          type="textarea"
          rows={8}
          placeholder="Describe the issue or request in detail"
          value={fields.description}
          disabled={submitting}
          error={!!errors.description}
          aria-invalid={!!errors.description}
          onChange={(_e: React.ChangeEvent<HTMLTextAreaElement>, value: string | number): void =>
            setField("description", String(value))
          }
        />
      </KuikaField>

      <KuikaCollapsePanel
        className="kuika-helpdesk-new-ticket__credentials"
        animated
        defaultActiveKey={fields.appMail || fields.appPassword ? ["appCredentials"] : []}
        items={[
          {
            key: "appCredentials",
            label: "Application Login",
            children: (
              <div className="kuika-flex-column--gap-m">
                <KuikaField label="Mail" htmlFor="helpdesk-new-ticket-app-mail" optional error={errors.appMail}>
                  <KuikaInput
                    id="helpdesk-new-ticket-app-mail"
                    data-testid="ticketAppMailInput"
                    inputMode="email"
                    autoComplete="off"
                    value={fields.appMail}
                    disabled={submitting}
                    error={!!errors.appMail}
                    onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number): void =>
                      setField("appMail", String(value))
                    }
                  />
                </KuikaField>
                <KuikaField
                  label="Password"
                  htmlFor="helpdesk-new-ticket-app-password"
                  optional
                  error={errors.appPassword}
                  help="The application login we can use to reproduce the problem. The password is stored encrypted; only the requester, the assignee and an admin can reveal it."
                >
                  <KuikaInput
                    id="helpdesk-new-ticket-app-password"
                    data-testid="ticketAppPasswordInput"
                    type="password"
                    autoComplete="new-password"
                    value={fields.appPassword}
                    disabled={submitting}
                    error={!!errors.appPassword}
                    onChange={(_e: React.ChangeEvent<HTMLInputElement>, value: string | number): void =>
                      setField("appPassword", String(value))
                    }
                  />
                </KuikaField>
              </div>
            )
          }
        ]}
      />

      <KuikaField
        label="Attachments"
        optional
        help={`Any file such as video, image or document · up to ${formatFileSize(limits.maxFileBytes)} per file`}
      >
        <HelpdeskDropzone
          hint="Drag files here or click to select"
          inputTestId="newTicketFileInput"
          disabled={submitting}
          onFiles={addFiles}
        />
        <HelpdeskPendingFiles
          files={files}
          disabled={submitting}
          onRemove={(removed: File): void =>
            setFiles((current: File[]): File[] => current.filter((f: File): boolean => f !== removed))
          }
        />
      </KuikaField>

      {progress && (
        <div className="kuika-helpdesk-progress" aria-live="polite">
          <KuikaSmallText>
            Uploading file ({progress.current}/{progress.total})
          </KuikaSmallText>
          <div
            className="kuika-helpdesk-progress__track"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={progress.percent}
          >
            <div className="kuika-helpdesk-progress__bar" style={{ width: `${progress.percent}%` }} />
          </div>
        </div>
      )}

      <div className="kuika-flex-end--gap-s">
        <KuikaButton type="secondary" color="neutral" onClick={onCancel} disabled={submitting}>
          Cancel
        </KuikaButton>
        <KuikaButton
          data-testid="sendButton"
          type="primary"
          onClick={submit}
          loading={submitting}
          disabled={submitting}
        >
          Submit Ticket
        </KuikaButton>
      </div>
    </div>
  );
};

export default HelpdeskNewTicketForm;
